import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useGolfApi, GolfOuting } from '../services/golfApi';
import { Calendar, MapPin, Users, Plus, Trophy, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

const GolfOutingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'in-progress' | 'completed'>('all');
  const golfApi = useGolfApi();

  const { data: outings = [], isLoading, error } = useQuery({
    queryKey: ['golf', 'outings'],
    queryFn: () => golfApi.getOutings(),
  });

  const filteredOutings = filter === 'all' 
    ? outings 
    : outings.filter(o => o.status === filter);

  const getStatusBadge = (status: string) => {
    const badges = {
      upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'in-progress': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      completed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    };
    return badges[status as keyof typeof badges] || badges.upcoming;
  };

  const getFormatLabel = (format: string) => {
    const labels = {
      scramble: 'Scramble',
      'best-ball': 'Best Ball',
      'stroke-play': 'Stroke Play',
    };
    return labels[format as keyof typeof labels] || format;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          Error loading golf outings. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Trophy className="h-10 w-10 text-green-600" />
              Golf Outings
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage golf outings, teams, and track scores in real-time
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/golf/courses')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <MapPin className="h-4 w-4" />
              Manage Courses
            </button>
            <button
              onClick={() => navigate('/golf/outings/create')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Outing
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
          {['all', 'upcoming', 'in-progress', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === f
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1).replace('-', ' ')}
              {f !== 'all' && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800">
                  {outings.filter(o => o.status === f).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Outings Grid */}
      {filteredOutings.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No golf outings found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Get started by creating your first golf outing
          </p>
          <button
            onClick={() => navigate('/golf/outings/create')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Create Golf Outing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOutings.map((outing) => (
            <div
              key={outing.outingId}
              onClick={() => navigate(`/golf/outings/${outing.outingId}`)}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all cursor-pointer overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold">{outing.name}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(outing.status)}`}>
                    {outing.status.replace('-', ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-green-100">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">{outing.courseName}</span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 space-y-4">
                {/* Date & Format */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">
                      {format(new Date(outing.date), 'MMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm">{getFormatLabel(outing.format)}</span>
                  </div>
                </div>

                {/* Participants & Teams */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {outing.participants?.length || 0} Players
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {outing.teams?.length || 0} Teams
                      </div>
                    </div>
                  </div>
                  
                  {outing.status === 'in-progress' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/golf/outings/${outing.outingId}/scorecard`);
                      }}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Score
                    </button>
                  )}
                  
                  {outing.status === 'completed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/golf/outings/${outing.outingId}/leaderboard`);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Results
                    </button>
                  )}
                </div>

                {/* Side Games Indicator */}
                {outing.sideGames && outing.sideGames.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Trophy className="h-3 w-3" />
                    {outing.sideGames.length} side game{outing.sideGames.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GolfOutingsPage;

